import { useSectionVisible } from 'appContainer/SectionVisible';
import {
	createClaim,
	getClaimDetails,
	getPrescriptions,
	onChangeClaim,
	resetClaim,
	setSelectedPage,
	setStatusCalculation,
} from 'feature/claims/claimSlice';
import { getMemberDetails } from 'feature/members/memberSlice';
import { resetUpload } from 'feature/upload/uploadSlice';
import { useQuery } from 'hooks/useQuery.hook';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppDispatch, RootState } from 'store/store';
import { BoxOverflow, Button, Stack, StepperUI, Typography } from 'styles';
import { IClaim } from 'types/Claim';
import { SECTIONS } from 'types/SectionsVisibility';
import { useTranslation } from 'utils/translations';
import { getShortDate } from 'utils/utils';
import Page0 from './Page0.component';
import Page1 from './Page1.component';
import Page2 from './Page2.component';
import Page3 from './Page3.component';

const NewClaimForm = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const query = useQuery();
	const claimUUID = query.get('claimUUID');
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const dispatch = useDispatch<AppDispatch>();
	const { subscriptionUuid: subscriptionUuidFromUrl } = useParams();

	const { member } = useSelector((store: RootState) => store.member);
	const locationInformation = useSelector(
		(store: RootState) => store.locationInformation,
	);
	const { profile } = useSelector((store: RootState) => store.user);

	const enableClaimReceipt = useSectionVisible(SECTIONS.NEWCLAIM_INVOICE, {
		customLayout: member?.carrier.customLayout || null,
	});

	const {
		claimData: formData,
		triggerRefresh,
		redirect,
		statusCalculation,
	} = useSelector((state: RootState) => state.claim);

	useEffect(() => {
		if (triggerRefresh && claimUUID) {
			dispatch(getClaimDetails({ claimUuid: claimUUID }));
		} else if (
			!formData.memberSubscriptionUuid &&
			!claimUUID &&
			member?.subscriptionUuid &&
			locationInformation.locationName
		) {
			const claimObj = {
				memberFullName: member?.memberName,
				beneficiaryMemberId: member?.memberId,
				memberId: member?.memberId,
				memberSubscriptionUuid: member?.subscriptionUuid,
				beneficiarySubscriptionUuid: member?.subscriptionUuid,
				providerId: profile?.providerId,
				claimDate: new URLSearchParams(location.search).get('dateOfService')!,
				rxChangeAttestation: !member?.rxChangeConfiguration.rxCheck ? 0 : null,
				carrierIntegrationFlow: member?.carrier.carrierIntegrationFlow, //TODO: not used, to be removed
				customLayout: member?.carrier.customLayout,
				receipt: enableClaimReceipt
					? {
							vatCode: locationInformation.taxId,
							name: locationInformation.legalEntityName,
						}
					: null,
			};

			dispatch(createClaim(claimObj));
			dispatch(getPrescriptions({}));
		}
	}, [
		claimUUID,
		dispatch,
		formData.memberSubscriptionUuid,
		location.search,
		member,
		profile?.providerId,
		triggerRefresh,
		locationInformation.taxId,
		locationInformation.legalEntityName,
		locationInformation.locationName,
		enableClaimReceipt,
	]);

	useEffect(() => {
		const isNewClaim = !formData.beneficiarySubscriptionUuid;
		const subscriptionUuid = isNewClaim
			? subscriptionUuidFromUrl!
			: formData.beneficiarySubscriptionUuid;
		const dateOfService = isNewClaim
			? new URLSearchParams(location.search).get('dateOfService')!
			: formData.claimDate;

		if (!subscriptionUuid || !dateOfService) {
			return;
		}

		dispatch(
			getMemberDetails({
				subscriptionUuid,
				dateOfService,
				callback: () => {},
			}),
		);
	}, [
		dispatch,
		formData.beneficiarySubscriptionUuid,
		formData.claimDate,
		location.search,
		member?.subscriptionUuid,
		subscriptionUuidFromUrl,
	]);

	const selectedPage = formData.step;

	const setFormData = (changeFunction: (prevValue: IClaim) => IClaim) => {
		dispatch(onChangeClaim(changeFunction));
	};

	const submitPage = () => {
		//we no longer save at each step
		// dispatch(saveClaim({ switchPage: true, redirect: false }));
		setFormData(form => ({ ...form, step: form.step + 1 }));
	};

	//lets save claimId on url as soon as its created
	useEffect(() => {
		if (formData.claimUUID && !claimUUID) {
			navigate(`${location.pathname}?claimUUID=${formData.claimUUID}`);
		}
	}, [claimUUID, formData.claimUUID, location.pathname, navigate]);

	//redirect at the end of evrything
	useEffect(() => {
		if (redirect) {
			navigate(`/view-claims`);
		}
		return () => {
			dispatch(resetClaim());
			dispatch(resetUpload());
		};
	}, [dispatch, navigate, redirect]);

	const carrier = {
		customLayout: formData.customLayout || null,
	};

	const steps = [
		useSectionVisible(SECTIONS.NEWCLAIM_TYPE_OF_SERVICE, carrier) &&
			SECTIONS.NEWCLAIM_TYPE_OF_SERVICE,
		useSectionVisible(SECTIONS.NEWCLAIM_PRESCRIPTION, carrier) &&
			SECTIONS.NEWCLAIM_PRESCRIPTION,
		useSectionVisible(SECTIONS.NEWCLAIM_PRICE_ESTIMATE, carrier) &&
			SECTIONS.NEWCLAIM_PRICE_ESTIMATE,
		useSectionVisible(SECTIONS.NEWCLAIM_INVOICE, carrier) &&
			SECTIONS.NEWCLAIM_INVOICE,
	].filter(Boolean);

	return (
		<Stack sx={{ height: '98%' }}>
			<Stack direction="row" pt="17px" columnGap="30px">
				<Stack direction="row" gap="0.2em">
					<Typography variant="value3">
						{tClientLabels('newClaim.subheader.patientName')}
					</Typography>
					<Typography variant="value1">{formData.memberFullName}</Typography>
				</Stack>
				<Stack direction="row" gap="0.2em">
					<Typography variant="value3">
						{tClientLabels('newClaim.subheader.dateOfService')}
					</Typography>
					<Typography variant="value1">
						{getShortDate(formData.claimDate)}
					</Typography>
				</Stack>
			</Stack>
			<StepperUI
				sx={{
					mb: '40px',
					width: steps.length === 3 ? '429px' : { sm: '531px', lg: '555px' },
				}}
				steps={steps.map((pageName, pageIndex) => ({
					title: tClientLabels(`newClaim.${pageName}`),
					page: pageIndex,
				}))}
				setSelectedPage={pageNumber => {
					dispatch(setSelectedPage(pageNumber));
				}}
				selectedPage={selectedPage || 0}
			/>
			{
				[
					<BoxOverflow variant="y" key="0">
						<Page0
							formData={formData}
							setFormData={setFormData}
							submitPage={submitPage}
							isLastPage={steps.length === 1}
						/>
					</BoxOverflow>,
					<BoxOverflow variant="y" key="1" id="page1-scroll">
						<Page1
							formData={formData}
							setFormData={setFormData}
							submitPage={submitPage}
							isLastPage={steps.length === 2}
						/>
					</BoxOverflow>,
					<>
						<Stack direction="row" mb={'12px'} pr={'35px'} gap="20px">
							<Typography variant="title6">
								{tClientLabels('newClaim.selectedPage.sectionTitile.priceEstimate')}
							</Typography>
							{['afterCalculation'].includes(statusCalculation) ? (
								<Button
									variant="linkDark"
									onClick={() => {
										dispatch(setStatusCalculation('onEdit'));
									}}
									sx={{ minWidth: 'auto' }}
								>
									{tClientLabels('newClaim.priceEstimate.edit')}
								</Button>
							) : null}
						</Stack>
						<BoxOverflow variant="y" key="2">
							<Page2
								formData={formData}
								setFormData={setFormData}
								submitPage={submitPage}
								isLastPage={steps.length === 3}
							/>
						</BoxOverflow>
					</>,
					<BoxOverflow variant="y" key="3">
						<Page3
							formData={formData}
							setFormData={setFormData}
							submitPage={submitPage}
							isLastPage={steps.length === 4}
						/>
					</BoxOverflow>,
				][selectedPage]
			}
		</Stack>
	);
};

export default NewClaimForm;
